import type { JSX } from "react";
import { StrictMode, lazy, Suspense, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { JoinPage } from "../routes/join/JoinPage.js";
import { registerPwa, type PwaHandle } from "./pwa.js";
import { loadBootstrap } from "./bootstrap.js";
import { currentRoute } from "./route.js";
import { onNavigate, watchHistory } from "./navigate.js";
import { startRum } from "./rum.js";
import { I18nProvider, useI18n } from "../i18n/react.js";
import { StatusBand } from "../ui/StatusBand.js";
import { RouteLoader } from "../ui/RouteLoader.js";
import { PanelBoundary } from "../ui/ErrorBoundary.js";
import { LocaleSwitch } from "../ui/LocaleSwitch.js";
import "../ui/tokens.css";
import "../ui/components.css";

/**
 * Uygulama kabuğu — Rapor 14 §14, Rapor 20 §6.1.
 *
 * ### ⭐ Kod bölme sınırı = ROTA sınırı
 * `join` **statik** (kritik yol, bütçe içinde); `room` **dinamik** — medya
 * motoru, video ızgarası ve sohbet yalnız gerçekten odaya girildiğinde yüklenir.
 *
 * ⚠️ Faz 1 kapsamı: gerçek bir router kütüphanesi **eklenmez**. İki rota
 *    vardır (`/j/:id` ve `/room/:id`) ve geçiş tam sayfa yüklemesidir; bir
 *    router 8–15 KB kritik yol maliyetiyle gelir ve karşılığı yoktur.
 */
const RoomPage = lazy(() =>
  import("../routes/room/RoomPage.js").then((m) => ({ default: m.RoomPage })),
);

/**
 * ⭐ Maliyet paneli (F4-13) — **dinamik**.
 *
 * ⚠️ Statik olsaydı, hiçbir zaman yönetici paneli açmayacak binlerce katılımcı
 *    bu kodu kritik yolda indirirdi. Panel Cloudflare Access arkasındadır ve
 *    katılımcıların çoğu onu hiç görmez.
 */
const UsagePage = lazy(() =>
  import("../routes/admin/UsagePage.js").then((m) => ({ default: m.UsagePage })),
);

/**
 * ⭐ Yönetici konsolu (`/admin`) — **dinamik**.
 *
 * ⚠️ Katılımcıların %99'u bu sayfayı hiç açmaz; oluşturma formu, tablo ve
 *    teşhis metinleri kritik yola giremez.
 */
const AdminPage = lazy(() =>
  import("../routes/admin/AdminPage.js").then((m) => ({ default: m.AdminPage })),
);

/**
 * ⭐ Tanıtım sayfası (`/`) — **dinamik**.
 *
 * ⚠️ Statik olsaydı, ürünün asıl girişi olan `/j/{id}` bağlantısını açan her
 *    katılımcı tanıtım metinlerini, SVG'leri ve stillerini de indirirdi.
 *    Tanıtım sayfası kritik yol DEĞİLDİR; davet bağlantısı kritik yoldur
 *    (Rapor 14 §3.2, §12).
 */
const LandingPage = lazy(() =>
  import("../routes/landing/LandingPage.js").then((m) => ({ default: m.LandingPage })),
);

/**
 * ⭐ Operatör paneli (`/operator`) — **ayrı ve dinamik**.
 *
 * ⚠️ Yönetici konsoluyla AYNI pakete konmaz. Müşteri yöneticisinin
 *    indirdiği JS, platform ekranlarının (kurum açma, SSO bağlama, operatör
 *    yönetimi) kodunu taşımamalıdır: ölü kod da indirilebilir koddur ve
 *    gizlemek paketlememek değildir.
 */
const OperatorPage = lazy(() =>
  import("../routes/operator/OperatorPage.js").then((m) => ({ default: m.OperatorPage })),
);

function App(): JSX.Element {
  const i18n = useI18n();
  /**
   * ⚠️ Yol artık **durumdur**, her render'da `location`'dan okunmaz.
   *    Okunsaydı `history.pushState` sonrası React hiçbir şeyin değiştiğini
   *    bilmez ve ekran eski rotada donardı.
   */
  const [path, setPath] = useState(() => location.pathname);
  const route = currentRoute(path);
  const inRoom = route === "room";
  const [pwa, setPwa] = useState<PwaHandle | null>(null);
  const [updateReady, setUpdateReady] = useState(false);
  const [offline, setOffline] = useState(() => !navigator.onLine);

  useEffect(() => {
    // ⚠️ `isBusy` toplantıdayken TRUE döner → güncelleme önerisi GÖSTERİLMEZ.
    //    Görüşmenin ortasında sayfa yenilemek medyayı koparır.
    const handle = registerPwa(
      () => setUpdateReady(true),
      () => location.pathname.startsWith("/room/"),
    );
    setPwa(handle);
    return () => handle.dispose();
  }, []);

  /**
   * ⚠️ RUM **kritik yolda** ama ~1 KB'dır ve `PerformanceObserver` yerel API'dir
   *    (`web-vitals` kütüphanesi 2–4 KB eklerdi). Ölçüm yalnız sayfa
   *    gizlendiğinde bir kez gönderilir; medya akarken ağ meşgul edilmez.
   */
  useEffect(() => startRum(), []);

  /**
   * ⚠️ İki kaynak da bağlanır: uygulama içi `navigate()` çağrıları ve
   *    tarayıcının geri/ileri düğmeleri. İkincisi olmadan kullanıcı odadan
   *    geri gittiğinde adres değişir ama ekran değişmez — sayfa donmuş görünür.
   */
  useEffect(() => onNavigate(setPath), []);
  useEffect(() => watchHistory(setPath), []);

  useEffect(() => {
    const on = (): void => setOffline(false);
    const off = (): void => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  return (
    <>
      {/*
        ⭐ **ATLAMA BAĞLANTISI** (WCAG 2.4.1 "Bypass Blocks").
        ⚠️ DOM'da İLK öğe olmak zorunda: klavye kullanıcısının ilk Tab'ı buna
           gelmelidir. Görsel olarak gizlidir, odaklanınca belirir — gizli
           kalsaydı fare kullanıcısını rahatsız etmeden klavye kullanıcısına
           da hiçbir şey vermezdi.
        ⚠️ Odada GÖSTERİLMEZ: oda tam ekran bir uygulamadır, atlanacak
           tekrarlayan bir başlık bloğu yoktur.
      */}
      {!inRoom ? (
        <a data-skip-link href="#main">
          {i18n.t("a11y.skipToContent")}
        </a>
      ) : null}

      {/*
        ⚠️ Kabuk bandı YALNIZ oda dışında gösterilir: oda kendi tek slotlu
        bandını yönetir ve ikisi üst üste binerse "tek slot" kuralı bozulur.
      */}
      {!inRoom ? (
        <StatusBand
          candidates={[
            offline && { level: "error" as const, message: i18n.t("status.offline") },
            updateReady && {
              level: "info" as const,
              message: i18n.t("status.updateReady"),
              action: { label: i18n.t("status.updateNow"), onAct: () => pwa?.applyUpdate() },
            },
          ]}
        />
      ) : null}

      {/*
        ⚠️ `join` DIŞINDAKİ her rota `Suspense` arkasındadır. Katılım sayfası
        statiktir ve bilinçli olarak öyle kalır: davet bağlantısını açan
        kullanıcı, ikinci bir ağ turu beklemeden formu görmelidir.
      */}
      {/* ⚠️ Dil değiştirici oda DIŞINDA: görüşme sırasında arayüz dilini
          değiştirmek, akan bir toplantıda gereksiz bir dikkat dağıtıcıdır. */}
      {!inRoom ? <LocaleSwitch /> : null}

      {/*
        ⭐ **Rota yükleyicisi, AÇILIŞ EKRANIYLA aynı dili konuşur.**
        🚨 Aşağıdaki `fallback` sol üstte duran 1em'lik jenerik bir daire +
           metindi. Kullanıcı açılışta marka işaretini görüp sonra bambaşka
           bir yükleyiciye geçiyordu: aynı bekleme, iki farklı yüz. Bir
           arayüzün "derlenmiş" hissetmesi, aynı şeyi her yerde aynı biçimde
           söylemesinden gelir.
      */}
      {route === "join" ? (
        <JoinPage />
      ) : (
        <Suspense fallback={<RouteLoader />}>
          {route === "room" ? <RoomPage /> : null}
          {route === "usage" ? <UsagePage /> : null}
          {route === "admin" ? <AdminPage /> : null}
          {route === "operator" ? <OperatorPage /> : null}
          {route === "landing" ? <LandingPage /> : null}
        </Suspense>
      )}
    </>
  );
}

const el = document.getElementById("root");
if (!el) throw new Error("root_element_missing");

/**
 * ⚠️ Yapılandırma render'dan **ÖNCE** yüklenir.
 *
 * `bootstrap()` senkron bir imzaya sahiptir ve onlarca yerden çağrılır; değer
 * yerleşmeden render başlarsa ilk kare varsayılan markayla çizilir ve hemen
 * ardından değişir (görünür bir marka atlaması).
 *
 * ⚠️ Hata durumunda **yine render edilir**: ağ hatası yüzünden beyaz ekran
 *    göstermek, varsayılan markayla açmaktan çok daha kötüdür.
 */
void loadBootstrap().finally(() => {
  createRoot(el).render(
    <StrictMode>
      <I18nProvider>
        {/*
        ⚠️ En dış sınır: bir rota tümüyle çökerse kullanıcı beyaz ekran yerine
        "yeniden yükle" görür. İç panellerin kendi sınırları vardır ve önce
        onlar yakalar — bu yalnız son çaredir.
      */}
        <PanelBoundary name="app">
          <App />
        </PanelBoundary>
      </I18nProvider>
    </StrictMode>,
  );
});
